/**
 * ----------------------------------------------------------------------------------------------------------------------
 * COLLECTIONS TABS
 * ----------------------------------------------------------------------------------------------------------------------
 *
 * Progressive enhancement for sections/collections-tabs.liquid.
 *
 * Every panel is rendered server-side, so switching tabs is a class change with no network request —
 * there is no loading state to design around and nothing to fail. This element manages which panel is
 * visible, keyboard navigation, the tab-strip arrows, and the product carousel inside each panel.
 *
 * Scrolling uses native CSS scroll-snap plus scrollBy({behavior:'smooth'}), rather than a JS animation
 * loop. That keeps momentum/trackpad/touch gestures native, stays smooth on low-end devices, and
 * respects prefers-reduced-motion through the stylesheet.
 *
 * Implemented as a custom element so it re-initialises automatically when the theme editor swaps a
 * section (connectedCallback fires on the new node) — no shopify:section:load listener needed.
 */

(function () {
  'use strict';

  if (window.customElements.get('collections-tabs')) return;

  var CollectionsTabs = class extends HTMLElement {
    connectedCallback() {
      this.tabList = this.querySelector('[data-ct-tablist]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-ct-tab]'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-ct-panel]'));
      this.prevButton = this.querySelector('[data-ct-prev]');
      this.nextButton = this.querySelector('[data-ct-next]');

      if (!this.tabs.length) return;

      this._onClick = this._onClick.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._updateArrows = this._updateArrows.bind(this);
      this._updateAllCarousels = this._updateAllCarousels.bind(this);

      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeyDown);

      if (this.tabList) {
        this.tabList.addEventListener('scroll', this._updateArrows, { passive: true });
      }

      // Each panel owns its own carousel track and pair of arrows.
      this.tracks = Array.prototype.slice.call(this.querySelectorAll('[data-ct-track]'));
      this.tracks.forEach(
        function (track) {
          track.addEventListener('scroll', this._updateAllCarousels, { passive: true });
        }.bind(this)
      );

      // Arrow visibility depends on whether content overflows, which changes with width.
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(
          function () {
            this._updateArrows();
            this._updateAllCarousels();
          }.bind(this)
        );
        if (this.tabList) this._resizeObserver.observe(this.tabList);
        this.tracks.forEach(function (t) {
          this._resizeObserver.observe(t);
        }, this);
      } else {
        window.addEventListener('resize', this._updateArrows);
        window.addEventListener('resize', this._updateAllCarousels);
      }

      this._updateArrows();
      this._updateAllCarousels();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeyDown);
      if (this._resizeObserver) this._resizeObserver.disconnect();
      else {
        window.removeEventListener('resize', this._updateArrows);
        window.removeEventListener('resize', this._updateAllCarousels);
      }
    }

    _onClick(event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var tab = target.closest('[data-ct-tab]');
      if (tab && this.contains(tab)) {
        event.preventDefault();
        this.select(this.tabs.indexOf(tab), { focus: false });
        return;
      }

      var carouselPrev = target.closest('[data-ct-cprev]');
      if (carouselPrev) {
        this._scrollTrack(carouselPrev, -1);
        return;
      }

      var carouselNext = target.closest('[data-ct-cnext]');
      if (carouselNext) {
        this._scrollTrack(carouselNext, 1);
      }
    }

    /** WAI-ARIA tabs pattern: arrows move between tabs, Home/End jump to the ends. */
    _onKeyDown(event) {
      var tab = event.target.closest ? event.target.closest('[data-ct-tab]') : null;
      if (!tab || !this.contains(tab)) return;

      var current = this.tabs.indexOf(tab);
      var next = null;

      switch (event.key) {
        case 'ArrowRight':
          next = (current + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
          next = (current - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(next, { focus: true });
    }

    select(index, options) {
      if (index < 0 || index >= this.tabs.length) return;
      var focus = options && options.focus;

      this.tabs.forEach(function (tab, i) {
        var active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        // Roving tabindex: only the active tab is reachable by Tab, arrows move within the set.
        tab.setAttribute('tabindex', active ? '0' : '-1');
        tab.classList.toggle('is-active', active);
      });

      this.panels.forEach(function (panel, i) {
        var active = i === index;
        panel.toggleAttribute('hidden', !active);
        panel.classList.toggle('is-active', active);
      });

      var activeTab = this.tabs[index];
      if (focus) activeTab.focus();

      this._scrollTabIntoView(activeTab);
      this._updateArrows();

      // A hidden track reports zero width, so its arrows can only be measured once visible.
      this._updateAllCarousels();
    }

    /** Keeps the selected tab visible in the horizontal strip without scrolling the page. */
    _scrollTabIntoView(tab) {
      if (!this.tabList) return;

      var listRect = this.tabList.getBoundingClientRect();
      var tabRect = tab.getBoundingClientRect();

      if (tabRect.left < listRect.left) {
        this.tabList.scrollBy({ left: tabRect.left - listRect.left - 16, behavior: 'smooth' });
      } else if (tabRect.right > listRect.right) {
        this.tabList.scrollBy({ left: tabRect.right - listRect.right + 16, behavior: 'smooth' });
      }
    }

    _scrollStrip(direction) {
      if (!this.tabList) return;
      var amount = Math.max(this.tabList.clientWidth * 0.7, 120);
      this.tabList.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    /**
     * Advances the carousel by whole cards rather than a fixed pixel amount, so a card is never left
     * half-visible at the edge. Falls back to a viewport-width scroll if card width can't be measured.
     */
    _scrollTrack(button, direction) {
      var carousel = button.closest('[data-ct-carousel]');
      if (!carousel) return;

      var track = carousel.querySelector('[data-ct-track]');
      if (!track) return;

      var first = track.firstElementChild;
      var step = track.clientWidth * 0.9;

      if (first) {
        var styles = window.getComputedStyle(track);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        var cardWidth = first.getBoundingClientRect().width + gap;
        if (cardWidth > 0) {
          // Move by as many whole cards as fit in the viewport, at least one.
          var perView = Math.max(1, Math.floor(track.clientWidth / cardWidth));
          step = cardWidth * perView;
        }
      }

      track.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    _updateAllCarousels() {
      var carousels = this.querySelectorAll('[data-ct-carousel]');
      Array.prototype.forEach.call(
        carousels,
        function (carousel) {
          var track = carousel.querySelector('[data-ct-track]');
          var prev = carousel.querySelector('[data-ct-cprev]');
          var next = carousel.querySelector('[data-ct-cnext]');
          if (!track) return;

          var overflowing = track.scrollWidth - track.clientWidth > 2;
          carousel.classList.toggle('ct--can-scroll', overflowing);

          if (!prev || !next) return;

          var left = track.scrollLeft;
          var max = track.scrollWidth - track.clientWidth;

          prev.disabled = !overflowing || left <= 1;
          next.disabled = !overflowing || left >= max - 1;
        }.bind(this)
      );
    }

    /**
     * Tab-strip arrows are hidden entirely when the strip fits, and individually disabled at each end,
     * so they never appear as dead controls.
     */
    _updateArrows() {
      if (!this.tabList) return;

      var overflowing = this.tabList.scrollWidth - this.tabList.clientWidth > 2;
      this.classList.toggle('ct--overflowing', overflowing);

      if (!this.prevButton || !this.nextButton) return;

      var left = this.tabList.scrollLeft;
      var max = this.tabList.scrollWidth - this.tabList.clientWidth;

      this.prevButton.disabled = !overflowing || left <= 1;
      this.nextButton.disabled = !overflowing || left >= max - 1;
    }
  };

  // Tab-strip arrows are plain buttons handled here rather than in _onClick, because they sit outside
  // the tablist and must not be confused with tabs.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var button = target.closest('[data-ct-prev], [data-ct-next]');
    if (!button) return;

    var root = button.closest('collections-tabs');
    if (!root || typeof root._scrollStrip !== 'function') return;

    root._scrollStrip(button.hasAttribute('data-ct-prev') ? -1 : 1);
  });

  window.customElements.define('collections-tabs', CollectionsTabs);
})();
